import { useMemo } from 'react';
import { SourcesDailyStatsResponse } from '../../../../shared/services/monitor/data/sources/MonitorDataSourcesService';

const MAX_LEGEND_LENGTH = 40;

const truncatePath = (path: string): string => {
  if (path.length <= MAX_LEGEND_LENGTH) return path;
  return path.slice(0, MAX_LEGEND_LENGTH - 3) + '...';
};

const extractPath = (source: string, domain: string): string => {
  try {
    const url = new URL(source.startsWith('http') ? source : `https://${source}`);
    const path = url.pathname + url.search + url.hash;
    return truncatePath(path || '/');
  } catch {
    const domainIndex = source.indexOf(domain);
    if (domainIndex >= 0) {
      const path = source.slice(domainIndex + domain.length);
      return truncatePath(path || '/');
    }
    return truncatePath(source);
  }
};

export const useSourceRetrievalCountMatrix = (
  sourcesDailyStats: SourcesDailyStatsResponse | undefined,
  domain: string
): (string | number)[][] | null => {
  return useMemo(() => {
    if (!sourcesDailyStats || Object.keys(sourcesDailyStats).length === 0 || !domain) {
      return null;
    }

    const sourceCountMap = new Map<string, number>();

    Object.values(sourcesDailyStats).forEach((daySources) => {
      daySources.forEach((s) => {
        sourceCountMap.set(s.source, (sourceCountMap.get(s.source) || 0) + s.count);
      });
    });

    if (sourceCountMap.size === 0) return null;

    const top10Sources = Array.from(sourceCountMap.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([source]) => source);

    const pathLabels = top10Sources.map((source) => extractPath(source, domain));

    const labelCounts = new Map<string, number>();
    const uniqueLabels = pathLabels.map((label) => {
      const count = labelCounts.get(label) || 0;
      labelCounts.set(label, count + 1);
      if (count > 0) return `${label} (${count + 1})`;
      return label;
    });

    const sortedDates = Object.keys(sourcesDailyStats).sort(
      (a, b) => new Date(a).getTime() - new Date(b).getTime()
    );

    const matrix: (string | number)[][] = [['date', ...uniqueLabels]];

    sortedDates.forEach((date) => {
      const daySources = sourcesDailyStats[date];
      const sourcesMap = new Map(daySources.map((s) => [s.source, s.count]));

      const row: (string | number)[] = [date];
      top10Sources.forEach((source) => {
        row.push(sourcesMap.get(source) || 0);
      });

      matrix.push(row);
    });

    return matrix;
  }, [sourcesDailyStats, domain]);
};
